import { cn } from "../lib";

function logoAssetPath(path: string, imageBaseUrl?: string): string {
	const normalized = path.startsWith("/") ? path : `/${path}`;
	if (!imageBaseUrl?.trim()) {
		return normalized;
	}
	const base = imageBaseUrl.replace(/\/$/, "");
	return `${base}${normalized}`;
}

export interface LogoProps {
	className?: string;
	/**
	 * Full wordmark (`logo_main_*` PNGs). When `false`, icon mark only (`logo_icon.png`), matching
	 * `apps/web/modules/saas/shared/components/Logo.tsx` header treatment.
	 * @default true
	 */
	withLabel?: boolean;
	/** Shorthand for icon-only mark; when `true`, overrides wordmark. */
	iconOnly?: boolean;
	/**
	 * Absolute site origin for raster `href` values (no trailing slash), e.g. `https://app.example.com`.
	 * Use in email and other contexts where `/images/...` is not resolveable; defaults to same-origin paths.
	 */
	imageBaseUrl?: string;
}

/**
 * SkillQuest mark / wordmark as **SVG** shells embedding the same PNG assets as
 * `apps/web/modules/saas/shared/components/Logo.tsx` (so mail, docs, and admin stay aligned with the app header).
 */
export function Logo({
	className,
	withLabel = true,
	iconOnly = false,
	imageBaseUrl,
}: LogoProps) {
	const markOnly = iconOnly || withLabel === false;
	const iconHref = logoAssetPath("/images/logo_icon.png", imageBaseUrl);
	const mainDarkHref = logoAssetPath(
		"/images/logo_main_dark.png",
		imageBaseUrl,
	);
	const mainLightHref = logoAssetPath("/images/logo_main.png", imageBaseUrl);

	if (markOnly) {
		return (
			<span
				role="img"
				aria-label="SkillQuest"
				className={cn(
					"inline-flex h-10 w-auto shrink-0 items-center",
					className,
				)}
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 120 120"
					aria-hidden="true"
					className="block h-full w-auto"
				>
					<image
						href={iconHref}
						width={120}
						height={120}
						preserveAspectRatio="xMidYMid meet"
					/>
				</svg>
			</span>
		);
	}

	return (
		<span
			role="img"
			aria-label="SkillQuest"
			className={cn(
				"inline-flex h-10 w-auto shrink-0 items-center",
				className,
			)}
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 360 120"
				aria-hidden="true"
				className="block h-full w-auto dark:hidden"
			>
				<image
					href={mainDarkHref}
					width={360}
					height={120}
					preserveAspectRatio="xMidYMid meet"
				/>
			</svg>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 360 120"
				aria-hidden="true"
				className="hidden h-full w-auto dark:block"
			>
				<image
					href={mainLightHref}
					width={360}
					height={120}
					preserveAspectRatio="xMidYMid meet"
				/>
			</svg>
		</span>
	);
}
